import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Switch } from '@headlessui/react';
import {
    EllipsisVerticalIcon,
    XMarkIcon,
    ArrowPathIcon,
    PlusIcon,
    EyeIcon,
    DocumentDuplicateIcon,
    Cog6ToothIcon,
    PencilSquareIcon,
    StarIcon,
    AdjustmentsHorizontalIcon,
    TrashIcon,
    EyeSlashIcon,
    CheckIcon,
    ChevronLeftIcon,
    ChevronRightIcon
} from '@heroicons/react/24/outline';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { motion, AnimatePresence } from 'framer-motion';
import Swal from 'sweetalert2';
import Header from '../components/Header';
import sessionCheck from "../session/userData";
import { encryptDataHybrid } from "../enc/useEncrypt";
import { getDeviceInfo } from '../utils/deviceInfo';

const API_URL = process.env.REACT_APP_API_URL;

const CATEGORY_MAPPING = {
    "Recharge & Utility": "1",
    "Payment Gateway": "2",
    "Bank Account": "3",
    "Recharge Plan": "4",
    "Message": "5",
    "Bill Fetch": "6",
    "rOffer": "7",
    "Utility Payment": "8",
    "Operator Fetch": "9",
    "Recharge Validation": "10",
    "Operator Form": "11"
};

const CATEGORY_DISPLAY_MAPPING = {
    "1": "Recharge & Bill Payment",
    "2": "Payment Gateway",
    "3": "Bank Account",
    "4": "Plan",
    "5": "Message",
    "6": "Bill Fetch",
    "7": "rOffer",
    "8": "Bill Payment",
    "9": "Operator State Fetch",
    "10": "Validation API",
    "11": "Operator IF"
};

const Provider = () => {
    const [providers, setProviders] = useState([]);
    const navigate = useNavigate();
    const [access_data, setAccess_data] = useState([]);
    const [showTokenModal, setShowTokenModal] = useState(false);
    const [currentToken, setCurrentToken] = useState('');
    const [currentCallbackURL, setCurrentCallbackURL] = useState('');
    const [currentResetToken, setCurrentResetToken] = useState('');
    const [settingResetLoading, setSettingResetLoading] = useState(false);
    const [isProviderModalOpen, setIsProviderModalOpen] = useState(false);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editedProvider, setEditedProvider] = useState(null);
    const [isAddNew, setIsAddNew] = useState(false);
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [activeConfigTab, setActiveConfigTab] = useState('recharge');
    const [activeTab, setActiveTab] = useState("Recharge & Utility");

    const [configData, setConfigData] = useState({
        recharge: { attributes: [], loading: false, loaded: false },
        balance: { attributes: [], loading: false, loaded: false },
        complaint: { attributes: [], loading: false, loaded: false },
        callback: { attributes: [], loading: false, loaded: false },
        status: { attributes: [], loading: false, loaded: false }
    });

    // Configuration modal state
    const [showConfigModalOther, setShowConfigModalOther] = useState(false);
    const [configAttributes, setConfigAttributes] = useState([]);
    const [configLoading, setConfigLoading] = useState(false);

    const [tableName, setTableName] = useState('');
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10
    });

    const tabs = Object.keys(CATEGORY_MAPPING);

    useEffect(() => {
        if (sessionCheck() == null) {
            navigate("../login", { replace: true });
        } else {
            setAccess_data(JSON.parse(sessionCheck()));
        }
    }, [navigate]);

    useEffect(() => {
        if (access_data?.username) {
            fetchProviders();
        }
    }, [access_data?.username, activeTab]);

    const fetchProviders = async () => {
        try {
            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    query: 'fetch-provider',
                    category: CATEGORY_MAPPING[activeTab]
                }
            };

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);
            if (!encryptedMessage) {
                toast.error("Encryption failed. Please try again.");
                return;
            }

            const response = await fetch(`${API_URL}provider-operation.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify({
                    encrypted_key: encryptedMessage.encryptedKey,
                    encrypted_data: encryptedMessage.encryptedData,
                }),
            });

            const result = await response.json();
            if (result.status === true) {
                setProviders(result.data);
                setPagination(prev => ({
                    ...prev,
                    currentPage: 1,
                    totalItems: result.data.length,
                    totalPages: Math.ceil(result.data.length / prev.itemsPerPage)
                }));
            } else {
                setProviders([]);
                toast.error(result.message || "Failed to fetch providers data.");
            }
        } catch (error) {
            console.error("Fetch providers error:", error);
            toast.error("An unexpected error occurred. Please try again.");
        }
    };

    const handleResetSetting = async (type) => {
        // Show confirmation dialog
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You are about to reset the token. This action cannot be undone!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, reset it!'
        });

        // If user cancels, exit the function
        if (!result.isConfirmed) return;

        setSettingResetLoading(true);
        try {
            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    query: type,
                    type: CATEGORY_MAPPING[activeTab],
                    provider_id: currentResetToken
                }
            };

            console.log(dataToEncrypt);

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);
            if (!encryptedMessage) {
                throw new Error("Encryption failed");
            }

            const response = await fetch(`${API_URL}provider-operation.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify({
                    encrypted_key: encryptedMessage.encryptedKey,
                    encrypted_data: encryptedMessage.encryptedData,
                }),
            });

            const result = await response.json();
            if (result.status === true) {
                toast.success("Settings reset successfully");
                fetchProviders();
            } else {
                throw new Error(result.message || "Reset failed");
            }
        } catch (error) {
            toast.error(error.message || "Failed to reset settings");
        } finally {
            setSettingResetLoading(false);
        }
    };

    const handleResetToken = async (type) => {
        // Show confirmation dialog
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You are about to reset the token. This action cannot be undone!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, reset it!'
        });

        // If user cancels, exit the function
        if (!result.isConfirmed) return;

        setSettingResetLoading(true);


        try {
            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    query: type,
                    type: CATEGORY_MAPPING[activeTab],
                    provider_id: currentResetToken
                }
            };

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);
            if (!encryptedMessage) {
                throw new Error("Encryption failed");
            }

            const response = await fetch(`${API_URL}provider-operation.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify({
                    encrypted_key: encryptedMessage.encryptedKey,
                    encrypted_data: encryptedMessage.encryptedData,
                }),
            });

            const result = await response.json();
            if (result.status === true) {
                toast.success("Token reset successfully");
                if (showTokenModal) {
                    setShowTokenModal(false);
                }
                fetchProviders();
            } else {
                throw new Error(result.message || "Token reset failed");
            }
        } catch (error) {
            toast.error(error.message || "Failed to reset token");
        } finally {
            setSettingResetLoading(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success("Copied to clipboard!");
    };

    const showToken = (token, callback_url, company_id) => {
        setCurrentToken(token);
        setCurrentCallbackURL(callback_url);
        setCurrentResetToken(company_id);
        setShowTokenModal(true);
    };

    const handleStatusChange = async (providerId, newStatus) => {
        const originalStatus = providers.find(provider => provider.company_id === providerId)?.status;
        const statusText = newStatus ? '1' : '0';

        try {
            setProviders(providers.map(provider =>
                provider.company_id === providerId ? { ...provider, status: statusText } : provider
            ));

            const response = await updateProviderStatus(providerId, statusText);
            if (response?.status !== true) {
                throw new Error(response?.message || 'Status update failed');
            }
            toast.success(`Status updated successfully`);
        } catch (error) {
            setProviders(providers.map(provider =>
                provider.company_id === providerId ? { ...provider, status: originalStatus } : provider
            ));
            toast.error(error.message || 'Failed to update status');
        }
    };

    const updateProviderStatus = async (providerId, newStatus) => {
        try {
            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    query: 'update-status',
                    company_id: providerId,
                    status: newStatus,
                    type: activeTab
                }
            };

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);
            if (!encryptedMessage) {
                throw new Error("Encryption failed");
            }

            const response = await fetch(`${API_URL}provider-operation.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify({
                    encrypted_key: encryptedMessage.encryptedKey,
                    encrypted_data: encryptedMessage.encryptedData,
                }),
            });

            return await response.json();
        } catch (error) {
            console.error('Status update error:', error);
            throw error;
        }
    };

    const makePrimary = async (providerId) => {
        try {
            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    query: 'make-primary',
                    company_id: providerId,
                }
            };

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);
            if (!encryptedMessage) {
                throw new Error("Encryption failed");
            }

            const response = await fetch(`${API_URL}provider-operation.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify({
                    encrypted_key: encryptedMessage.encryptedKey,
                    encrypted_data: encryptedMessage.encryptedData,
                }),
            });

            const result = await response.json();
            if (result.status === true) {
                toast.success("Provider set as primary successfully");
                fetchProviders();
            } else {
                throw new Error(result.message || "Failed to set primary provider");
            }
        } catch (error) {
            toast.error(error.message || "An unexpected error occurred while setting primary provider");
        }
    };

    const deleteProvider = async (providerId, providerName) => {
        try {
            const result_confirm = await Swal.fire({
                title: 'Are you sure?',
                text: `You are about to delete ${providerName}. This action cannot be undone.`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, delete it!'
            });

            if (!result_confirm.isConfirmed) {
                return;
            }

            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    query: 'delete-provider',
                    company_id: providerId
                }
            };

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);
            if (!encryptedMessage) {
                throw new Error("Encryption failed");
            }

            const response = await fetch(`${API_URL}provider-operation.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify({
                    encrypted_key: encryptedMessage.encryptedKey,
                    encrypted_data: encryptedMessage.encryptedData,
                }),
            });

            const result = await response.json();
            if (result.status === true) {
                toast.success("Provider deleted successfully");
                setProviders(providers.filter(provider => provider.company_id !== providerId));
            } else {
                throw new Error(result.message || "Failed to delete provider");
            }
        } catch (error) {
            toast.error(error.message || "An unexpected error occurred while deleting the provider");
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, currentPage: newPage }));
        }
    };

    const getPaginatedData = () => {
        const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
        const endIndex = startIndex + pagination.itemsPerPage;
        return providers.slice(startIndex, endIndex);
    };

    const openProviderModal = (providerId, isNew = false) => {
        if (isNew) {
            setIsAddNew(true);
            setEditedProvider({
                company_id: '',
                company_name: '',
                ip_address: '',
                request_origin: '',
                status: '1',
                type: CATEGORY_MAPPING[activeTab],
                type_display: activeTab
            });
        } else {
            const provider = providers.find(p => p.company_id === providerId);
            if (provider) {
                setSelectedProvider(provider);
                setEditedProvider({
                    ...provider,
                    type_display: CATEGORY_DISPLAY_MAPPING[provider.type] || provider.type
                });
                setIsAddNew(false);
            }
        }
        setIsProviderModalOpen(true);
        setIsEditing(true);
    };

    const closeProviderModal = () => {
        setIsProviderModalOpen(false);
        setSelectedProvider(null);
        setIsEditing(false);
        setIsAddNew(false);
    };

    const handleSaveProvider = async () => {
        try {
            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    query: isAddNew ? 'add-provider' : 'update-provider',
                    company_id: editedProvider.company_id,
                    company_name: editedProvider.company_name,
                    ip_address: editedProvider.ip_address,
                    request_origin: editedProvider.request_origin,
                    status: editedProvider.status,
                    type: editedProvider.type
                }
            };

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);
            if (!encryptedMessage) {
                toast.error("Encryption failed. Please try again.");
                return;
            }

            const response = await fetch(`${API_URL}provider-operation.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify({
                    encrypted_key: encryptedMessage.encryptedKey,
                    encrypted_data: encryptedMessage.encryptedData,
                }),
            });

            const result = await response.json();
            if (result.status === true) {
                toast.success(isAddNew ? "Provider added successfully" : "Provider updated successfully");
                fetchProviders();
                closeProviderModal();
            } else {
                toast.error(result.message || "Failed to save provider");
            }
        } catch (error) {
            console.error("Save provider error:", error);
            toast.error("An unexpected error occurred. Please try again.");
        }
    };

    const handleFieldChange = (field, value) => {
        setEditedProvider(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleConfigChange = (index, value) => {
        const updatedAttributes = [...configData[activeConfigTab].attributes];
        updatedAttributes[index].value = value;
        setConfigData(prev => ({
            ...prev,
            [activeConfigTab]: {
                ...prev[activeConfigTab],
                attributes: updatedAttributes
            }
        }));
    };

    const providerConfig = (providerId, category, companyName) => {
        if (category === '1') {
            navigate(`/recharge-api-config?providerId=${providerId}&companyName=${companyName}`);
        } else {
            fetchProviderConfig(providerId);
        }
    };

    const fetchProviderConfig = async (providerId) => {
        setConfigLoading(true);
        setShowConfigModalOther(true);
        setSelectedProvider(providers.find(p => p.company_id === providerId));

        try {
            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    query: 'get-provider-config',
                    company_id: providerId,
                    type: CATEGORY_MAPPING[activeTab] || ''
                }
            };

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);
            if (!encryptedMessage) {
                throw new Error("Encryption failed");
            }

            const response = await fetch(`${API_URL}provider-operation.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify({
                    encrypted_key: encryptedMessage.encryptedKey,
                    encrypted_data: encryptedMessage.encryptedData,
                }),
            });

            const result = await response.json();
            if (result.status === true) {
                // Transform the data object into an array of attributes
                const configData = result.data[0]; // Assuming first item in array
                const attributes = Object.entries(configData)
                    .filter(([key]) => key !== 'sl' && key !== 'company_id') // Exclude non-config fields
                    .map(([name, value]) => ({
                        name,
                        value,
                        type: name.toLowerCase().includes('key') || name.toLowerCase().includes('secret') ? 'password' : 'text'
                    }));

                setConfigAttributes(attributes);
                setTableName(result.table_name || '');
            } else {
                throw new Error(result.message || "Failed to fetch configuration");
            }
        } catch (error) {
            toast.error(error.message || "Failed to load configuration");
            setShowConfigModalOther(false);
        } finally {
            setConfigLoading(false);
        }
    };

    useEffect(() => {
        if (isProviderModalOpen || showConfigModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isProviderModalOpen, showConfigModal]);

    const saveProviderConfig = async () => {
        try {
            // Transform attributes back to key-value object
            const configData = {};
            configAttributes.forEach(attr => {
                if (attr.type !== 'section') {
                    configData[attr.name] = attr.value;
                }
            });

            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    query: 'save-provider-config',
                    company_id: selectedProvider.company_id,
                    type: activeTab,
                    table_name: tableName,
                    config_data: configData
                }
            };

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);
            if (!encryptedMessage) {
                throw new Error("Encryption failed");
            }

            const response = await fetch(`${API_URL}save-provider-config.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify({
                    encrypted_key: encryptedMessage.encryptedKey,
                    encrypted_data: encryptedMessage.encryptedData,
                }),
            });

            const result = await response.json();
            if (result.status === true) {
                toast.success("Configuration saved successfully");
                setShowConfigModal(false);
            } else {
                throw new Error(result.message || "Failed to save configuration");
            }
        } catch (error) {
            toast.error(error.message || "Failed to save configuration");
        }
    };

    useEffect(() => {
        if (settingResetLoading) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [settingResetLoading]);


    return (
        <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-white">
            <div className="flex flex-col flex-1">
                <Header />
                <main className="flex-1 overflow-auto">
                    {/* Tabs Section */}
                    <main className="flex-1 p-6">
                        <div className="max-w-7xl mx-auto">
                            <div className="flex items-center justify-between mb-6">
                                <button
                                    onClick={() => navigate(-1)}
                                    className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-600"
                                >
                                    <ChevronLeftIcon className="h-5 w-5 mr-1" />
                                    Back
                                </button>
                                <h2 className="text-xl font-semibold">
                                    API Provider
                                </h2>
                                <div></div>
                            </div>
                            <div className="flex flex-wrap gap-2 pb-2">
                                {tabs.map((tab) => (
                                    <button
                                        key={tab}
                                        onClick={() => setActiveTab(tab)}
                                        className={`px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap ${activeTab === tab
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                                            }`}
                                    >
                                        {tab}
                                    </button>
                                ))}
                            </div>
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-medium">Providers</h2>
                                <div className="flex space-x-2">
                                    {(activeTab === 'Recharge & Utility' || activeTab === 'Bank Account') && (
                                        <button
                                            onClick={() => openProviderModal(null, true)}
                                            className="px-3 py-2 text-sm text-white bg-green-600 rounded hover:bg-green-700 flex items-center"
                                        >
                                            <PlusIcon className="h-4 w-4 mr-1" />
                                            Add Provider
                                        </button>
                                    )}


                                    <button
                                        onClick={() => handleResetSetting('multiple-setting-change')}
                                        className="px-3 py-2 text-sm text-white bg-red-600 rounded hover:bg-red-700 flex items-center"
                                    >
                                        <ArrowPathIcon className="h-4 w-4 mr-1" />
                                        Reset Setting
                                    </button>
                                    <button
                                        onClick={() => handleResetToken('multiple-token-change')}
                                        className="px-3 py-2 text-sm text-white bg-yellow-600 rounded hover:bg-yellow-700 flex items-center"
                                    >
                                        <ArrowPathIcon className="h-4 w-4 mr-1" />
                                        Reset Tokens
                                    </button>
                                </div>
                            </div>

                            {/* Providers Table */}
                            <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-x-auto mb-6">
                                {/* Provider Table */}
<div className="overflow-x-auto rounded-lg shadow">
  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
    <thead className="bg-gray-100 dark:bg-gray-800">
      <tr>
        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">Provider ID</th>
        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">Name</th>
        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">Status</th>
        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">Identity Token</th>
        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">IP Address</th>
        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">Primary</th>
        <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300">Actions</th>
      </tr>
    </thead>
    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
      {getPaginatedData().map((provider) => (
        <tr key={provider.company_id} className="bg-white dark:bg-gray-700">
          {/* Provider ID */}
          <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{provider.company_id}</td>

          {/* Name */}
          <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{provider.company_name}</td>

          {/* Status */}
          <td className="px-3 py-2">
            <div className="flex items-center">
              <Switch
                checked={provider.status === "1"}
                onChange={(enabled) => handleStatusChange(provider.company_id, enabled)}
                className={`${provider.status === "1" ? "bg-green-500" : "bg-red-500"} relative inline-flex h-5 w-9 items-center rounded-full`}
              >
                <span className="sr-only">Toggle status</span>
                <span className={`${provider.status === "1" ? "translate-x-4" : "translate-x-1"} inline-block h-3 w-3 transform rounded-full bg-white transition-transform`} />
              </Switch>
            </div>
          </td>

          {/* Identity Token */}
          <td className="px-3 py-2 text-sm">
            <button
              onClick={() => showToken(provider.identity_token, provider.callback_url, provider.company_id)}
              className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-600"
              title="View Token"
            >
              <EyeIcon className="h-4 w-4 mr-1" />
              View
            </button>
          </td>

          {/* IP Address */}
          <td className="px-3 py-2 text-sm flex items-center">
            <span>{provider.ip_address || "N/A"}</span>
            {provider.ip_address && (
              <button
                onClick={() => copyToClipboard(provider.ip_address)}
                className="ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                title="Copy IP"
              >
                <DocumentDuplicateIcon className="h-4 w-4" />
              </button>
            )}
          </td>

          {/* Primary */}
          <td className="px-3 py-2 text-sm">
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                provider.isPrimary === "1"
                  ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                  : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
              }`}
            >
              {provider.isPrimary === "1" ? "Yes" : "No"}
            </span>
          </td>

          {/* Actions */}
          <td className="px-3 py-2 text-center">
            <div className="flex justify-center gap-2">
              <button
                onClick={() => openProviderModal(provider.company_id)}
                className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600"
                title="Edit Provider"
              >
                <PencilSquareIcon className="h-5 w-5 text-yellow-500" />
              </button>

              <button
                onClick={() => providerConfig(provider.company_id, provider.category, provider.company_name)}
                className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600"
                title="Configure Provider"
              >
                <Cog6ToothIcon className="h-5 w-5 text-blue-500" />
              </button>

              {provider.isPrimary === "0" && (
                <button
                  onClick={() => makePrimary(provider.company_id)}
                  className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600"
                  title="Make Primary"
                >
                  <StarIcon className="h-5 w-5 text-yellow-500" />
                </button>
              )}

              {activeTab === "Recharge & Utility" && (
                <>
                  <button
                    onClick={() => window.open(`/plan-slap?providerId=${provider.company_id}`, "_blank")}
                    className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600"
                    title="Margin Settings"
                  >
                    <AdjustmentsHorizontalIcon className="h-5 w-5 text-green-500" />
                  </button>

                  <button
                    onClick={() => deleteProvider(provider.company_id, provider.company_name)}
                    className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600"
                    title="Delete Provider"
                  >
                    <TrashIcon className="h-5 w-5 text-red-500" />
                  </button>
                </>
              )}
            </div>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
</div>

                                {/* Empty State */}
                                {getPaginatedData().length === 0 && (
                                    <div className="p-4 text-center text-sm text-gray-500">
                                        No providers found
                                    </div>
                                )}
                                {/* Pagination */}
                                <div className="flex items-center justify-between px-4 py-3 mt-4 border-t border-gray-200 dark:border-gray-700">
                                    <div className="flex-1 flex justify-between sm:hidden">
                                        <button
                                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                                            disabled={pagination.currentPage === 1}
                                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600"
                                        >
                                            Previous
                                        </button>
                                        <button
                                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                                            disabled={pagination.currentPage === pagination.totalPages}
                                            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600"
                                        >
                                            Next
                                        </button>
                                    </div>
                                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                        <div>
                                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                                Showing <span className="font-medium">{(pagination.currentPage - 1) * pagination.itemsPerPage + 1}</span> to{' '}
                                                <span className="font-medium">
                                                    {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}
                                                </span>{' '}
                                                of <span className="font-medium">{pagination.totalItems}</span> results
                                            </p>
                                        </div>
                                        <div>
                                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                                <button
                                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                                    disabled={pagination.currentPage === 1}
                                                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                                                >
                                                    <span className="sr-only">Previous</span>
                                                    <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                                                </button>

                                                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                                    let pageNum;
                                                    if (pagination.totalPages <= 5) {
                                                        pageNum = i + 1;
                                                    } else if (pagination.currentPage <= 3) {
                                                        pageNum = i + 1;
                                                    } else if (pagination.currentPage >= pagination.totalPages - 2) {
                                                        pageNum = pagination.totalPages - 4 + i;
                                                    } else {
                                                        pageNum = pagination.currentPage - 2 + i;
                                                    }

                                                    return (
                                                        <button
                                                            key={pageNum}
                                                            onClick={() => handlePageChange(pageNum)}
                                                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${pagination.currentPage === pageNum
                                                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600 dark:bg-gray-600 dark:text-white'
                                                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300'
                                                                }`}
                                                        >
                                                            {pageNum}
                                                        </button>
                                                    );
                                                })}

                                                <button
                                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                                    disabled={pagination.currentPage === pagination.totalPages}
                                                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                                                >
                                                    <span className="sr-only">Next</span>
                                                    <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                                                </button>
                                            </nav>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </main>
                </main>
            </div>
            {/* Provider Modal */}
            <AnimatePresence>
                {isProviderModalOpen && (
                    <>
                        <motion.div
                            className="fixed inset-0 z-40 bg-black bg-opacity-30 backdrop-blur-sm"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={closeProviderModal}
                        />
                        <motion.div
                            className="fixed inset-0 z-50 overflow-y-auto"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <div className="flex items-center justify-center min-h-screen px-4 text-center sm:block sm:p-0">
                                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                                    &#8203;
                                </span>

                                <motion.div
                                    className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full relative z-50"
                                    initial={{ scale: 0.95, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0.95, opacity: 0 }}
                                >
                                    <div className="bg-white dark:bg-gray-800 pb-4 flex flex-col" style={{ maxHeight: '90vh' }}>
                                        <div className="flex justify-between items-center border-b dark:border-gray-700 p-4 sticky top-0 bg-white dark:bg-gray-800 z-10 mb-2">
                                            <h3 className="text-lg leading-6 font-medium text-dark">
                                                {isAddNew ? 'Add New Provider' : `Provider Details - ${selectedProvider?.company_name}`}
                                            </h3>
                                            <button
                                                className="text-dark hover:text-gray-100"
                                                onClick={closeProviderModal}
                                            >
                                                <XMarkIcon className="h-6 w-6" />
                                            </button>
                                        </div>

                                        <div className="overflow-y-auto pr-2 -mr-2 flex-grow">
                                            <div className="grid grid-cols-1 gap-4 pb-2 px-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        Provider ID
                                                    </label>
                                                    {isEditing ? (
                                                        <input
                                                            value={editedProvider.company_id || ''}
                                                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-gray-100 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
                                                            disabled
                                                        />
                                                    ) : (
                                                        <div className="text-sm text-gray-900 dark:text-gray-200 py-2 px-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                                                            {selectedProvider.company_id || 'N/A'}
                                                        </div>
                                                    )}
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        Provider Name
                                                    </label>
                                                    {isEditing ? (
                                                        <input
                                                            value={editedProvider.company_name || ''}
                                                            onChange={(e) => handleFieldChange('company_name', e.target.value)}
                                                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
                                                        />
                                                    ) : (
                                                        <div className="text-sm text-gray-900 dark:text-gray-200 py-2 px-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                                                            {selectedProvider.company_name || 'N/A'}
                                                        </div>
                                                    )}
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        Type
                                                    </label>
                                                    {isEditing ? (
                                                        <div className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-gray-100 dark:bg-gray-700">
                                                            {editedProvider.type_display || activeTab}
                                                        </div>
                                                    ) : (
                                                        <div className="text-sm text-gray-900 dark:text-gray-200 py-2 px-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                                                            {selectedProvider.type_display || activeTab}
                                                        </div>
                                                    )}
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        IP Address
                                                    </label>
                                                    {isEditing ? (
                                                        <input
                                                            value={editedProvider.ip_address || ''}
                                                            onChange={(e) => handleFieldChange('ip_address', e.target.value)}
                                                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
                                                        />
                                                    ) : (
                                                        <div className="text-sm text-gray-900 dark:text-gray-200 py-2 px-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                                                            {selectedProvider.ip_address || 'N/A'}
                                                        </div>
                                                    )}
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        Request Origin
                                                    </label>
                                                    {isEditing ? (
                                                        <input
                                                            value={editedProvider.request_origin || ''}
                                                            onChange={(e) => handleFieldChange('request_origin', e.target.value)}
                                                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
                                                        />
                                                    ) : (
                                                        <div className="text-sm text-gray-900 dark:text-gray-200 py-2 px-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                                                            {selectedProvider.request_origin || 'N/A'}
                                                        </div>
                                                    )}
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        Status
                                                    </label>
                                                    {isEditing ? (
                                                        <Switch
                                                            checked={editedProvider.status === '1'}
                                                            onChange={(enabled) => handleFieldChange('status', enabled ? '1' : '0')}
                                                            className={`${editedProvider.status === '1' ? 'bg-green-500' : 'bg-red-500'} relative inline-flex h-6 w-11 items-center rounded-full`}
                                                        >
                                                            <span className="sr-only">Toggle status</span>
                                                            <span
                                                                className={`${editedProvider.status === '1' ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                                                            />
                                                        </Switch>
                                                    ) : (
                                                        <div className="text-sm text-gray-900 dark:text-gray-200 py-2 px-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                                                            {selectedProvider.status === '1' ? 'Active' : 'Inactive'}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mt-4 pt-4 flex justify-end gap-3 border-t">
                                            {isEditing ? (
                                                <div className='px-4 gap-4 flex'>
                                                    <button
                                                        onClick={closeProviderModal}
                                                        className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        onClick={handleSaveProvider}
                                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                                    >
                                                        {isAddNew ? 'Add Provider' : 'Save Changes'}
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => setIsEditing(true)}
                                                    className="px-4 mx-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 transition-colors"
                                                >
                                                    Edit Provider
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </motion.div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* Token Modal */}
            <AnimatePresence>
                {showTokenModal && (
                    <>
                        <motion.div
                            className="fixed inset-0 z-40 bg-black bg-opacity-30 backdrop-blur-sm"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setShowTokenModal(false)}
                        />
                        <motion.div
                            className="fixed inset-0 z-50 flex items-center justify-center p-4"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ type: "spring", damping: 25, stiffness: 300 }}
                        >
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-medium">Identity</h3>
                                    <button
                                        onClick={() => setShowTokenModal(false)}
                                        className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                                    >
                                        <XMarkIcon className="h-6 w-6" />
                                    </button>
                                </div>
                                <h5 className='text-sm font-medium'>Token</h5>
                                <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-md mb-4">
                                    <div className="flex justify-between items-center">
                                        <code className="text-sm break-all">{currentToken}</code>
                                        <div className='flex gap-2'>
                                            {currentToken && (
                                                <button
                                                    onClick={() => copyToClipboard(currentToken)}
                                                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-600"
                                                >
                                                    <DocumentDuplicateIcon className="h-5 w-5" />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => handleResetToken('single-token-change')}
                                                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-600"
                                            >
                                                <ArrowPathIcon className="h-5 w-5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                {currentCallbackURL && (
                                    <>
                                        <h5 className='text-sm font-medium'>Callback URL</h5>
                                        <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-md mb-4">
                                            <div className="flex justify-between items-center">
                                                <code className="text-sm break-all">{currentCallbackURL}</code>
                                                <div className='flex gap-2'>
                                                    {currentCallbackURL && (
                                                        <button
                                                            onClick={() => copyToClipboard(currentCallbackURL)}
                                                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-600"
                                                        >
                                                            <DocumentDuplicateIcon className="h-5 w-5" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}

                                <div className="flex justify-end space-x-4 mt-4">
                                    <button
                                        onClick={() => handleResetSetting('single-setting-change')} // Add your reset function here
                                        className="bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors"
                                    >
                                        Reset Settings
                                    </button>
                                    <button
                                        onClick={() => setShowTokenModal(false)}
                                        className="bg-gray-300 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* Configuration Modal - Except Recharge & Bill Payment */}
            <AnimatePresence>
                {showConfigModalOther && (
                    <>
                        <motion.div
                            className="fixed inset-0 z-40 bg-black bg-opacity-30 backdrop-blur-sm"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setShowConfigModal(false)}
                        />
                        <motion.div
                            className="fixed inset-0 z-50 flex items-center justify-center p-4"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ type: "spring", damping: 25, stiffness: 300 }}
                        >
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                                {/* Fixed Header */}
                                <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
                                    <h3 className="text-lg font-medium">
                                        {selectedProvider?.company_name} - Recharge & Bill Payment Configuration
                                    </h3>
                                    <button
                                        onClick={() => setShowConfigModalOther(false)}
                                        className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                                    >
                                        <XMarkIcon className="h-6 w-6" />
                                    </button>
                                </div>

                                {/* Scrollable Body */}
                                <div className="overflow-y-auto p-6 flex-1">
                                    {configLoading ? (
                                        <div className="flex justify-center items-center py-8">
                                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="mb-4">
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                    Table Name
                                                </label>
                                                <input
                                                    value={tableName}
                                                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-gray-100 dark:bg-gray-700"
                                                    disabled
                                                />
                                            </div>

                                            <div className="space-y-6">
                                                {configAttributes.map((attr, index) => {
                                                    if (attr.type === 'section') {
                                                        return (
                                                            <div key={index} className="pt-4">
                                                                <h4 className="text-lg font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                                                                    {attr.name}
                                                                </h4>
                                                            </div>
                                                        );
                                                    }

                                                    return (
                                                        <div>
                                                            <div key={index} className="grid grid-cols-12 gap-4 items-center">
                                                                <div className="col-span-4">
                                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                                        {attr.name.replace(/_/g, ' ')}
                                                                    </label>
                                                                </div>
                                                                <div className="col-span-8">
                                                                    <div className="relative">
                                                                        <input
                                                                            type={attr.type}
                                                                            value={attr.value || ''}
                                                                            onChange={(e) => handleConfigChange(index, e.target.value)}
                                                                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 pr-10"
                                                                            placeholder={`Enter ${attr.name.replace(/_/g, ' ')}`}
                                                                        />
                                                                        {/* Password Toggle */}
                                                                        {attr.type === 'password' && (
                                                                            <button
                                                                                type="button"
                                                                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                                                                onClick={() => {
                                                                                    const updatedAttributes = [...configAttributes];
                                                                                    updatedAttributes[index].type =
                                                                                        updatedAttributes[index].type === 'password' ? 'text' : 'password';
                                                                                    setConfigAttributes(updatedAttributes);
                                                                                }}
                                                                            >
                                                                                {attr.type === 'password' ? (
                                                                                    <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                                                                                ) : (
                                                                                    <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                                                                                )}
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* Fixed Footer */}
                                <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                                    <button
                                        onClick={() => setShowConfigModalOther(false)}
                                        className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={saveProviderConfig}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                    >
                                        Save Configuration
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* Full Screen Loading Spinner */}
            <AnimatePresence>
                {settingResetLoading && (
                    <motion.div
                        className="fixed inset-0 z-[100] bg-black/30 backdrop-blur-md flex items-center justify-center"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                    >
                        <div className="flex flex-col items-center justify-center space-y-4">
                            {/* Double-ring spinner with rounded edges */}
                            <div className="relative h-16 w-16">
                                {/* Background ring */}
                                <div className="absolute inset-0 rounded-full border-4 border-white/10"></div>

                                {/* Animated ring with rounded edges */}
                                <motion.div
                                    className="absolute inset-0 rounded-full border-4 border-transparent border-t-white border-r-white"
                                    animate={{ rotate: 360 }}
                                    transition={{
                                        repeat: Infinity,
                                        ease: "linear",
                                        duration: 1
                                    }}
                                    style={{
                                        borderTopLeftRadius: "9999px",
                                        borderTopRightRadius: "9999px",
                                        borderBottomLeftRadius: "9999px",
                                        borderBottomRightRadius: "9999px"
                                    }}
                                />
                            </div>

                            {/* Loading text with fade animation */}
                            <motion.p
                                className="text-white font-medium text-sm tracking-wide"
                                initial={{ opacity: 0, y: 5 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 }}
                            >
                                Loading...
                            </motion.p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <ToastContainer />
        </div>
    );
};

export default Provider;